import { jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { useThemeProps } from "@mui/material";
import { useSlotProps } from "../../../base/index.js";
import { useCalendarConfiguration } from "../context/CalendarRoot.js";
import {
  useComputeDaysByMonth,
  useComputeMonthsData,
  useFocusOnSelection,
  useNavigatorProps
} from "../context/hooks/index.js";
import { calcCalendarWidth } from "../context/utils.js";
import { rangeCalendarName, useRangeCalendarUtilityClasses } from "./classes.js";
import { Day } from "./components/Day.js";
import { Month } from "./components/Month.js";
import { MonthsContainer } from "./components/MonthsContainer.js";
import { Navigator } from "./components/Navigator.js";
import { Root } from "./components/Root.js";
import { useSelectionData } from "./hooks/useSelectionData.js";
import { useUpdateSelection } from "./hooks/useUpdateSelection.js";
function RangeCalendar(inProps) {
  const props = useThemeProps({
    name: rangeCalendarName,
    props: inProps
  });
  const {
    sx,
    testId = `${rangeCalendarName}-root`,
    onChange,
    className,
    components,
    selectionEnd,
    selectionStart,
    componentsProps,
    navigationIcons,
    focusOnSelection,
    maxIntervalLength,
    lastSelectableDate
  } = props;
  useFocusOnSelection(selectionStart, focusOnSelection);
  const config = useCalendarConfiguration();
  const {
    layout: { monthGap }
  } = config;
  const width = calcCalendarWidth(config);
  const { selection: selectionRecord, selectableInterval } = useSelectionData({
    selectionEnd,
    selectionStart,
    maxIntervalLength,
    lastSelectableDate
  });
  const months = useComputeMonthsData();
  const navProps = useNavigatorProps();
  const daysByMonth = useComputeDaysByMonth({ months });
  const handleOnClick = useUpdateSelection(onChange, selectionEnd, selectionStart);
  const ownerState = props;
  const utilityClasses = useRangeCalendarUtilityClasses(ownerState);
  const rootSlotProps = useSlotProps({
    className: [className, utilityClasses.root],
    ownerState,
    elementType: Root,
    additionalProps: {
      as: components?.Root,
      sx,
      width,
      "data-tidal": rangeCalendarName,
      "data-testid": testId
    },
    externalSlotProps: componentsProps?.root
  });
  const navSlotProps = useSlotProps({
    className: utilityClasses.navigator,
    ownerState,
    elementType: Navigator,
    additionalProps: {
      as: components?.Navigator,
      nextIcon: navigationIcons?.nextMonth,
      prevIcon: navigationIcons?.prevMonth
    },
    externalSlotProps: componentsProps?.navigator
  });
  const monthsContainerSlotProps = useSlotProps({
    className: utilityClasses.monthsContainer,
    ownerState,
    elementType: MonthsContainer,
    additionalProps: { as: components?.MonthsContainer, monthGap },
    externalSlotProps: componentsProps?.monthsContainer
  });
  const monthSlotProps = useSlotProps({
    className: utilityClasses.month,
    ownerState,
    elementType: Month,
    additionalProps: { as: components?.Month },
    externalSlotProps: componentsProps?.month
  });
  const daySlotProps = useSlotProps({
    className: utilityClasses.day,
    ownerState,
    elementType: Day,
    additionalProps: { as: components?.Day },
    externalSlotProps: componentsProps?.day
  });
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, children: [
    /* @__PURE__ */ jsx(Navigator, { ...navSlotProps, ...navProps }),
    /* @__PURE__ */ jsx(MonthsContainer, { ...monthsContainerSlotProps, children: months.map(
      (month) => daysByMonth[month.id] && /* @__PURE__ */ createElement(
        Month,
        {
          ...monthSlotProps,
          key: month.id,
          days: daysByMonth[month.id],
          onClick: handleOnClick,
          renderDay: ({ day }) => {
            const id = day.type === "day" ? day.id : "";
            const selection = selectionRecord[id];
            const canSelect = selectableInterval ? selectableInterval.has(id) : true;
            const hydratedDay = day.type === "day" ? {
              ...day,
              disabled: !canSelect
            } : day;
            return /* @__PURE__ */ jsx(Day, { ...daySlotProps, day: hydratedDay, selection });
          }
        }
      )
    ) })
  ] });
}
export {
  RangeCalendar
};
//# sourceMappingURL=RangeCalendar.js.map
